import type { ActivityLevel, Objective, Sex } from "./goals";

export type MealItem = {
  name: string;
  portion_g: number;
  calories: number;
  protein_g: number;
};

export type Meal = {
  id: string;
  capturedAt: string | number | Date;
  imageKey: string;
  imageHash: string;
  caption: string | null;
  calories: number;
  proteinG: number;
  items: MealItem[];
  confidence: "high" | "medium" | "low";
  notes: string | null;
  modelMeta: unknown;
  edited: boolean;
  createdAt: string | number | Date;
};

export type DaySummary = {
  day: string;
  calories: number;
  protein_g: number;
  meals: number;
};

export type SummaryResponse = {
  days: DaySummary[];
  goal: { calories: number; protein_g: number };
  tz: string;
};

export type Goals = {
  caloriesGoal: number;
  proteinGoalG: number;
  sex?: Sex | null;
  age?: number | null;
  heightCm?: number | null;
  weightKg?: number | null;
  activity?: ActivityLevel | null;
  objective?: Objective | null;
  goalsAuto?: boolean;
};

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || res.statusText);
  }
  return res.json() as Promise<T>;
}

export { ApiError };

export const api = {
  async login(password: string): Promise<void> {
    await request<{ ok: true }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },
  async logout(): Promise<void> {
    await request<{ ok: true }>("/api/auth/logout", { method: "POST" });
  },
  async me(): Promise<{ authenticated: boolean }> {
    try {
      return await request<{ authenticated: boolean }>("/api/auth/me");
    } catch {
      return { authenticated: false };
    }
  },
  meals(params: { from?: Date; to?: Date; limit?: number } = {}): Promise<{ meals: Meal[] }> {
    const u = new URLSearchParams();
    if (params.from) u.set("from", params.from.toISOString());
    if (params.to) u.set("to", params.to.toISOString());
    if (params.limit) u.set("limit", String(params.limit));
    return request<{ meals: Meal[] }>(`/api/meals?${u.toString()}`);
  },
  patchMeal(id: string, body: Partial<Pick<Meal, "items" | "caption">>) {
    return request<{ meal: Meal }>(`/api/meals/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  deleteMeal(id: string) {
    return request<{ ok: true }>(`/api/meals/${id}`, { method: "DELETE" });
  },
  summary(days: number, tz: string) {
    return request<SummaryResponse>(`/api/summary?days=${days}&tz=${encodeURIComponent(tz)}`);
  },
  summaryForMonth(month: Date, tz: string) {
    const m = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
    return request<SummaryResponse>(
      `/api/summary?month=${m}&tz=${encodeURIComponent(tz)}`,
    );
  },
  goals() {
    return request<{ goals: Goals }>("/api/goals");
  },
  putGoals(body: Goals) {
    return request<{ goals: Goals }>("/api/goals", {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  imageUrl(key: string): string {
    return `${API_BASE}/api/images/${key}`;
  },
};

export function browserTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}
